import React, { useCallback, useState } from 'react';
import classNames from 'classnames';
import { StatusCodes } from 'http-status-codes';
import { Button, Clickable, Input } from '@moda/om';
import { useForm } from '@moda/portal-stanchions';
import { CONFIG } from '../../config';
import { Link } from '../Link';

import './ForgotPassword.scss';

type ForgotPasswordProps = {
  className?: string;
  fullPage?: boolean;
  onClickSignIn?: () => void;
};

type ForgotPasswordFormValues = {
  forgotPasswordEmail: string;
};

const FORGOT_PASSWORD_MESSAGE =
  'Enter your email and you will receive a message with a link to reset your password.';
export const FORGOT_PASSWORD_SUBMIT = 'SUBMIT EMAIL';
const FORGOT_PASSWORD_TITLE = 'Forgot Password';
const FORGOT_PASSWORD_URL = `${CONFIG.MODA_API_URL}/v3.5/users/reset_password`;
export const FORGOT_PASSWORD_SUCCESS =
  'If the account exists, you will receive an email shortly with instructions on how to reset your password.';
export const SIGN_IN_TEXT = 'SIGN IN';

export const validations = {
  forgotPasswordEmail: {
    email: {
      message: 'Invalid email address.',
      value: true
    },
    required: {
      message: 'Please enter an email address.',
      value: true
    }
  }
};

const submitForgotPassword = async (email: string) => {
  const res = await fetch(FORGOT_PASSWORD_URL, {
    method: 'POST',
    mode: 'cors',
    cache: 'no-cache',
    credentials: 'same-origin',
    redirect: 'follow',
    referrer: 'no-referrer',
    headers: { 'Content-Type': 'application/vnd.api+json' },
    body: JSON.stringify({ data: { type: 'users', attributes: { email } } })
  });

  const data = await res.json();

  if (res.status !== StatusCodes.NOT_FOUND && data.errors && data.errors[0]) {
    return data.errors[0].detail;
  }

  return FORGOT_PASSWORD_SUCCESS;
};

export const ForgotPassword: React.FC<ForgotPasswordProps> = ({
  className,
  fullPage,
  onClickSignIn
}) => {
  const [result, setResult] = useState('');
  const onSubmit = useCallback(
    (values: ForgotPasswordFormValues) => {
      submitForgotPassword(values.forgotPasswordEmail)
        .then(message => setResult(message))
        .catch(error => setResult(error.message));
    },
    [setResult]
  );
  const { errors: fieldErrors, handleSubmit } = useForm({ onSubmit, validations });
  const forgotPasswordClassName = classNames('ForgotPassword', className, {
    'ForgotPassword--fullPage': fullPage
  });

  return (
    <form className={forgotPasswordClassName} data-testid="forgotPassword" onSubmit={handleSubmit}>
      <div className="ForgotPassword__content">
        <div className="ForgotPassword__result">{result}</div>
        <div className="ForgotPassword__header">
          <h1 className="ForgotPassword__title">{FORGOT_PASSWORD_TITLE}</h1>
          <div className="ForgotPassword__signIn">
            {onClickSignIn ? (
              <Clickable className="ForgotPassword__link" onClick={onClickSignIn} type="button">
                {SIGN_IN_TEXT}
              </Clickable>
            ) : (
              <Link className="ForgotPassword__link" to="/register">
                {SIGN_IN_TEXT}
              </Link>
            )}
          </div>
        </div>
        <div className="ForgotPassword__message">{FORGOT_PASSWORD_MESSAGE}</div>
        <Input
          className="ForgotPassword__input"
          error={fieldErrors.forgotPasswordEmail?.message}
          id="forgotPasswordEmail"
          placeholder="Email"
          type="text"
        />
        <Button className="ForgotPassword__button" type="submit">
          {FORGOT_PASSWORD_SUBMIT}
        </Button>
      </div>
    </form>
  );
};
